/**
 * TerritoriesModal - View and manage default quote settings per county
 * Ported from legacy territoriesAndDefaultQuotes.jsx
 * Responsive table (desktop) + card layout (mobile)
 */

import { useState, useEffect, useCallback } from 'react';
import { PencilIcon, MapPinIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getCountiesByAgency } from '../../../../services/oathTransferBondService';
import { formatCurrency } from '../shared/formatUtils';
import { OathModal, OathSpinner, OathAlert } from '../../../shared/OathUI';
import TerritoryEditModal from './TerritoryEditModal';

const TerritoriesModal = ({ isOpen, onClose, selectedAgency }) => {
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState(null);

  const fetchCounties = useCallback(async () => {
    if (!selectedAgency?.agencyId) return;
    setLoading(true);
    setError('');

    const response = await getCountiesByAgency(selectedAgency.agencyId);
    if (response.success) {
      setCounties(response.data || []);
    } else {
      setError(response.error || 'Failed to load territories.');
    }
    setLoading(false);
  }, [selectedAgency?.agencyId]);

  useEffect(() => {
    if (isOpen) {
      fetchCounties();
    }
  }, [isOpen, fetchCounties]);

  const handleEdit = (county) => {
    setSelectedCounty(county);
    setEditModalOpen(true);
  };

  const handleEditSave = () => {
    fetchCounties();
  };

  const formatHours = (minutes) => {
    if (!minutes) return '-';
    return `${(minutes / 60).toFixed(1)}h`;
  };

  return (
    <>
      <OathModal
        isOpen={isOpen}
        onClose={onClose}
        title="Territories & Default Quotes"
        size="xl"
      >
        <div className="space-y-4">
          {/* Agency name */}
          {selectedAgency && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">{selectedAgency.dbaName || selectedAgency.agencyName}</span>
            </p>
          )}

          {/* Description */}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            View and edit your default quote values and territory settings for each county.
            These defaults are used when submitting new quotes.
          </p>

          {/* Error */}
          {error && <OathAlert variant="error" message={error} />}

          {/* Loading */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <OathSpinner size="lg" />
            </div>
          ) : counties.length === 0 ? (
            <div className="text-center py-12">
              <MapPinIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No territories found for this agency.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400">State</th>
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400">County</th>
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400 text-right">Exec. Fee</th>
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400 text-right">Misc Fee</th>
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400 text-right">Travel Fee</th>
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400 text-right">Quote Dur.</th>
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400 text-right">Post Time</th>
                      <th className="pb-2 pr-3 font-medium text-gray-500 dark:text-gray-400 text-center">Preferred</th>
                      <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-center">Edit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {counties.map((county) => (
                      <tr key={county.agentCountyID} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-2 pr-3 text-gray-900 dark:text-white">{county.state}</td>
                        <td className="py-2 pr-3 text-gray-900 dark:text-white font-medium">{county.county}</td>
                        <td className="py-2 pr-3 text-right text-gray-700 dark:text-gray-300">{formatCurrency(county.tbondExecutionFeeQuote)}</td>
                        <td className="py-2 pr-3 text-right text-gray-700 dark:text-gray-300">{formatCurrency(county.tbondMisQuote)}</td>
                        <td className="py-2 pr-3 text-right text-gray-700 dark:text-gray-300">{formatCurrency(county.tbondTravelQuote)}</td>
                        <td className="py-2 pr-3 text-right text-gray-700 dark:text-gray-300">{formatHours(county.quoteDuration)}</td>
                        <td className="py-2 pr-3 text-right text-gray-700 dark:text-gray-300">{formatHours(county.timeToPost)}</td>
                        <td className="py-2 pr-3 text-center">
                          {county.isPreferred ? (
                            <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400 mx-auto" />
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="py-2 text-center">
                          <button
                            onClick={() => handleEdit(county)}
                            className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            title={`Edit ${county.county}`}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {counties.map((county) => (
                  <div
                    key={county.agentCountyID}
                    className="border border-gray-200 dark:border-gray-700 rounded-xl p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{county.county}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{county.state}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {county.isPreferred && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded text-xs font-medium">
                            <CheckIcon className="h-3 w-3" /> Preferred
                          </span>
                        )}
                        <button
                          onClick={() => handleEdit(county)}
                          className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Exec.</p>
                        <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(county.tbondExecutionFeeQuote)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Misc</p>
                        <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(county.tbondMisQuote)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Travel</p>
                        <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(county.tbondTravelQuote)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Quote Dur.</p>
                        <p className="font-medium text-gray-900 dark:text-white">{formatHours(county.quoteDuration)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Post Time</p>
                        <p className="font-medium text-gray-900 dark:text-white">{formatHours(county.timeToPost)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Count */}
              <p className="text-xs text-gray-400 dark:text-gray-500 pt-2">
                {counties.length} {counties.length === 1 ? 'territory' : 'territories'}
              </p>
            </>
          )}
        </div>
      </OathModal>

      {/* Edit Modal */}
      <TerritoryEditModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCounty(null);
        }}
        countyData={selectedCounty}
        onSave={handleEditSave}
      />
    </>
  );
};

export default TerritoriesModal;
